import React, { useContext } from "react";
import { FaCheckCircle, FaLightbulb, FaHandsHelping, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../TrainingGlobal.css";
import BannerImage from "./iso.jpg";

const ISO14001: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate('/booking/iso-14001');
    } else {
      navigate('/login', { state: { from: '/booking/iso-14001' } });
    }
  };

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO --- */}
        <div className="training-hero" style={{ backgroundImage: `url(${BannerImage})` }}>
          <span className="hero-tag">ISO Training</span>
          <h1 className="hero-title">ISO 14001 Auditor</h1>
          <p className="hero-subtitle">
            Ensuring environmental safety and compliance
          </p>
          <button onClick={handleBooking} className="btn-hero">
            Book Now <span style={{ fontSize: '1.2em' }}>→</span>
          </button>
        </div>

        {/* --- CONTENT --- */}
        <div className="detail-content">

          <div className="content-article">
            <div className="article-section">
              <h2 className="article-heading">Introduction to ISO 14001:2015</h2>
              <p className="article-text">
                ISO 14001:2015 is an international standard for environmental management systems (EMS). It provides a framework for organizations to protect the environment, reduce waste, and improve sustainability. The standard emphasizes compliance with environmental regulations, risk management, and continuous improvement. By implementing ISO 14001, organizations demonstrate their commitment to environmental stewardship and gain a competitive advantage.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Key Principles of ISO 14001:2015</h2>
              <p className="article-text">
                The key principles include environmental protection, compliance with legal requirements, continuous improvement, and stakeholder engagement. Environmental protection focuses on minimizing negative impacts on the environment. Compliance ensures adherence to regulations and standards. Continuous improvement drives organizations to enhance their environmental performance over time. Stakeholder engagement fosters collaboration and builds trust with customers, employees, and communities.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">EMS PDCA Cycle</h2>
              <p className="article-text">
                The EMS PDCA (Plan-Do-Check-Act) cycle is a continuous improvement model used in environmental management. It involves planning environmental objectives, implementing processes, monitoring performance, and taking corrective actions. For example, an organization may use the PDCA cycle to reduce energy consumption by analyzing usage patterns and implementing energy-efficient practices. This iterative approach ensures ongoing enhancement of environmental performance.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Structure of ISO 14001:2015</h2>
              <p className="article-text">
                ISO 14001:2015 follows the High-Level Structure (HLS), making it compatible with other ISO standards. The clauses include context of the organization, leadership, planning, support, operation, performance evaluation, and improvement. This structure ensures a systematic approach to environmental management. Organizations can integrate ISO 14001 with other standards, such as ISO 9001, for a unified management system.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Environmental Aspects and Impacts</h2>
              <p className="article-text">
                Identifying environmental aspects and their impacts helps organizations minimize their ecological footprint. For example, understanding the impact of waste disposal or energy consumption enables organizations to implement eco-friendly practices. Regular assessments and monitoring ensure that environmental objectives are met, contributing to long-term sustainability and regulatory compliance. This process is essential for achieving sustainability goals.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Safe Operational Procedures</h2>
              <p className="article-text">
                Safe operational procedures provide guidelines for performing tasks in an environmentally responsible manner. For example, procedures for handling hazardous materials may include proper storage, labeling, and disposal methods. These guidelines reduce risks, ensure compliance with environmental standards, and protect both employees and the environment. Regular training on these procedures ensures consistent implementation.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">The Environmental Policy</h2>
              <p className="article-text">
                The environmental policy outlines an organization's commitment to environmental protection. It serves as a foundation for setting objectives and implementing an effective EMS. For example, a policy may include commitments to reduce greenhouse gas emissions, conserve water, and promote recycling. A well-defined environmental policy demonstrates leadership and accountability, aligning the organization with sustainability goals.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Hazardous Waste Management</h2>
              <p className="article-text">
                Hazardous waste management involves identifying, handling, and disposing of hazardous materials safely. For example, organizations must segregate hazardous waste, use appropriate containers, and follow regulatory guidelines for disposal. Proper management ensures compliance with regulations, protects the environment, and minimizes risks to employees and communities. Regular audits and training enhance the effectiveness of hazardous waste management practices.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Control of Significant Aspects</h2>
              <p className="article-text">
                Controlling significant environmental aspects involves implementing measures to reduce their impact. For example, installing air filtration systems to control emissions from manufacturing processes minimizes environmental harm. Regular monitoring ensures the effectiveness of these controls, and continuous improvement efforts address emerging challenges. Effective control of significant aspects aligns organizations with environmental objectives and regulatory requirements.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Emergency Preparedness</h2>
              <p className="article-text">
                Emergency preparedness involves planning and training for potential environmental emergencies, such as chemical spills or natural disasters. For example, conducting spill response drills and providing employees with emergency contact information enhances readiness. Regular reviews and updates to emergency plans ensure that organizations are prepared for evolving risks and scenarios. Effective preparedness minimizes the impact of emergencies on the environment and operations.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">5R Principle for Environmental Protection</h2>
              <p className="article-text">
                The 5R principle (Reduce, Reuse, Recycle, Recover, and Refuse) promotes sustainable practices and resource conservation. For example, reducing waste by optimizing production processes, reusing materials where possible, and recycling waste products contribute to environmental protection. Adopting the 5R principle helps organizations minimize waste, conserve resources, and align with sustainability goals.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Life Cycle Perspective</h2>
              <p className="article-text">
                A life cycle perspective considers the environmental impacts of a product or process from creation to disposal. For example, designing products with recyclable materials reduces waste and conserves resources. This approach promotes sustainable practices, aligns with stakeholder expectations, and enhances the organization's reputation. Adopting a life cycle perspective helps organizations achieve long-term environmental and business objectives.
              </p>
            </div>
          </div>

          {/* --- SIDEBAR --- */}
          <div className="content-sidebar">
            <div className="sidebar-widget">
              <h4 className="widget-title">Key Elements</h4>
              <ul className="key-list">
                <li><FaCheckCircle className="check-icon" /> Environmental Protection</li>
                <li><FaLightbulb className="check-icon" /> Compliance</li>
                <li><FaHandsHelping className="check-icon" /> Sustainable Practices</li>
                <li><FaBullhorn className="check-icon" /> Continuous Improvement</li>
              </ul>
            </div>

            <div className="sidebar-widget">
              <h4 className="widget-title">Why ISO 14001?</h4>
              <p style={{ color: '#aaa', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Demonstrate environmental stewardship and ensure compliance with global standards.
              </p>
            </div>

            <div className="sidebar-widget" style={{ background: 'linear-gradient(135deg, #111 0%, #222 100%)', border: '1px solid #333' }}>
              <h4 className="widget-title">Get Certified</h4>
              <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '0.9rem' }}>
                Start your journey towards standard compliance.
              </p>

              <button
                onClick={handleBooking}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Book Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ISO14001;

